import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card, Button } from "@destroy/ui";
import {
  api,
  type MonitoringStats,
  type RequestLogResponse,
  type RequestLogEntry,
  type KvUsageStats,
  type MonitoringTimeline,
  type TimelineBucket,
  type RequestSource,
  type ErrorGroup,
  type EndpointPerformance,
  type EmailPipelineStats,
  type UptimeStats,
  type DurableObjectStats,
  type DbSizeStats,
  type AlertsData,
  ApiError,
} from "@/lib/api";
import {
  LuLoader,
  LuRefreshCw,
  LuSearch,
  LuFilter,
  LuChevronLeft,
  LuChevronRight,
  LuX,
  LuTrendingUp,
  LuChartBar,
  LuServer,
  LuGlobe,
  LuDownload,
  LuRadio,
  LuTriangleAlert,
  LuMail,
  LuShield,
  LuDatabase,
  LuBell,
  LuWifi,
} from "react-icons/lu";

type TimeRange = "1h" | "6h" | "24h" | "7d";

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: "1h", label: "1 hour" },
  { value: "6h", label: "6 hours" },
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
];

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-blue-500/20 text-blue-400",
  POST: "bg-emerald-500/20 text-emerald-400",
  PUT: "bg-amber-500/20 text-amber-400",
  PATCH: "bg-orange-500/20 text-orange-400",
  DELETE: "bg-red-500/20 text-red-400",
};

const AUTO_REFRESH_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 30, label: "30s" },
  { value: 60, label: "60s" },
  { value: 120, label: "2m" },
];

type SourceFilter = "all" | RequestSource;

const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "system", label: "System" },
  { value: "sdk", label: "SDK" },
  { value: "agent", label: "Agent" },
  { value: "external", label: "External" },
];

const SOURCE_COLORS: Record<string, string> = {
  system: "text-blue-400 bg-blue-500/10",
  sdk: "text-purple-400 bg-purple-500/10",
  agent: "text-amber-400 bg-amber-500/10",
  external: "text-emerald-400 bg-emerald-500/10",
};

// ─── SVG Line Chart ────────────────────────────────────────────────

interface ChartSeries {
  data: number[];
  color: string;
  label: string;
}

function LineChart({
  labels,
  series,
  height = 180,
  yFormat,
}: {
  labels: string[];
  series: ChartSeries[];
  height?: number;
  yFormat?: (v: number) => string;
}) {
  const [tooltip, setTooltip] = useState<{
    x: number;
    index: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(600);

  // Measure actual container width for accurate mouse mapping
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const padding = { top: 12, right: 12, bottom: 28, left: 48 };
  const width = containerWidth;

  const allValues = series.flatMap((s) => s.data);
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(...allValues, 0);
  const valueRange = maxVal - minVal || 1;

  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const getX = (i: number) =>
    padding.left + (labels.length > 1 ? (i / (labels.length - 1)) * chartW : chartW / 2);
  const getY = (v: number) =>
    padding.top + chartH - ((v - minVal) / valueRange) * chartH;

  const buildPath = (data: number[]) => {
    if (data.length === 0) return "";
    return data
      .map((v, i) => `${i === 0 ? "M" : "L"}${getX(i).toFixed(1)},${getY(v).toFixed(1)}`)
      .join(" ");
  };

  const buildArea = (data: number[]) => {
    if (data.length === 0) return "";
    const linePath = data
      .map((v, i) => `${i === 0 ? "M" : "L"}${getX(i).toFixed(1)},${getY(v).toFixed(1)}`)
      .join(" ");
    return `${linePath} L${getX(data.length - 1).toFixed(1)},${(padding.top + chartH).toFixed(1)} L${getX(0).toFixed(1)},${(padding.top + chartH).toFixed(1)} Z`;
  };

  // Y-axis ticks (4 ticks)
  const yTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let i = 0; i <= 3; i++) {
      ticks.push(minVal + (valueRange / 3) * i);
    }
    return ticks;
  }, [minVal, valueRange]);

  // X-axis ticks (show ~6 labels)
  const xTickIndices = useMemo(() => {
    if (labels.length <= 6) return labels.map((_, i) => i);
    const step = Math.ceil(labels.length / 6);
    const indices: number[] = [];
    for (let i = 0; i < labels.length; i += step) indices.push(i);
    if (indices[indices.length - 1] !== labels.length - 1) indices.push(labels.length - 1);
    return indices;
  }, [labels.length]);

  const formatLabel = (label: string) => {
    // "2026-02-27T14:30" → "14:30"  or  "2026-02-27T14" → "14:00"
    const timePart = label.split("T")[1] || label;
    if (timePart.length <= 2) return `${timePart}:00`;
    return timePart;
  };

  const fmt = yFormat || ((v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toString());

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!containerRef.current || labels.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const idx = Math.round(((mouseX - padding.left) / chartW) * (labels.length - 1));
    const clampedIdx = Math.max(0, Math.min(labels.length - 1, idx));
    setTooltip({ x: getX(clampedIdx), index: clampedIdx });
  };

  if (labels.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs text-[var(--color-text-muted)]" style={{ height }}>
        No data for this time range
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* Grid lines */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={getY(tick)}
              y2={getY(tick)}
              stroke="var(--color-border)"
              strokeWidth="0.5"
              strokeDasharray={i === 0 ? "0" : "4 3"}
            />
            <text
              x={padding.left - 6}
              y={getY(tick)}
              textAnchor="end"
              dominantBaseline="middle"
              fill="var(--color-text-subtle)"
              fontSize="9"
              fontFamily="monospace"
            >
              {fmt(tick)}
            </text>
          </g>
        ))}

        {/* X-axis labels */}
        {xTickIndices.map((i) => (
          <text
            key={i}
            x={getX(i)}
            y={height - 4}
            textAnchor="middle"
            fill="var(--color-text-subtle)"
            fontSize="9"
            fontFamily="monospace"
          >
            {formatLabel(labels[i])}
          </text>
        ))}

        {/* Area fills */}
        {series.map((s, si) => (
          <path
            key={`area-${si}`}
            d={buildArea(s.data)}
            fill={s.color}
            opacity="0.08"
          />
        ))}

        {/* Lines */}
        {series.map((s, si) => (
          <path
            key={`line-${si}`}
            d={buildPath(s.data)}
            fill="none"
            stroke={s.color}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Tooltip crosshair */}
        {tooltip && (
          <>
            <line
              x1={tooltip.x}
              x2={tooltip.x}
              y1={padding.top}
              y2={padding.top + chartH}
              stroke="var(--color-text-subtle)"
              strokeWidth="0.5"
              strokeDasharray="3 2"
            />
            {series.map((s, si) => (
              <circle
                key={si}
                cx={tooltip.x}
                cy={getY(s.data[tooltip.index] ?? 0)}
                r="3"
                fill={s.color}
                stroke="var(--color-surface)"
                strokeWidth="1.5"
              />
            ))}
          </>
        )}
      </svg>

      {/* Tooltip popup */}
      {tooltip && (
        <div
          className="absolute z-10 pointer-events-none bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 shadow-lg"
          style={{
            left: tooltip.x,
            top: 4,
            transform: tooltip.x > width * 0.7 ? "translateX(-100%)" : "translateX(0)",
          }}
        >
          <p className="text-[10px] text-[var(--color-text-subtle)] mb-0.5 font-mono">
            {formatLabel(labels[tooltip.index])}
          </p>
          {series.map((s, si) => (
            <div key={si} className="flex items-center gap-1.5 text-[10px]">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-[var(--color-text-muted)]">{s.label}:</span>
              <span className="text-[var(--color-text)] font-medium font-mono">
                {fmt(s.data[tooltip.index] ?? 0)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-1.5 justify-center">
        {series.map((s, i) => (
          <div key={i} className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)]">
            <span className="w-2 h-0.5 rounded" style={{ backgroundColor: s.color }} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Helper Components ─────────────────────────────────────────────

function StatusBadge({ status }: { status: number }) {
  let color = "text-[var(--color-text-muted)]";
  if (status >= 200 && status < 300) color = "text-emerald-400";
  else if (status >= 300 && status < 400) color = "text-blue-400";
  else if (status >= 400 && status < 500) color = "text-amber-400";
  else if (status >= 500) color = "text-red-400";
  return <span className={`font-mono text-xs font-medium ${color}`}>{status}</span>;
}

function StatCard({
  label,
  value,
  subValue,
  icon: Icon,
  variant,
}: {
  label: string;
  value: string | number;
  subValue?: string;
  icon: React.ComponentType<{ className?: string }>;
  variant?: "error";
}) {
  return (
    <Card padding="md">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          variant === "error"
            ? "bg-red-500/10"
            : "bg-[var(--color-elevated)] border border-[var(--color-border)]"
        }`}>
          <Icon className={`w-4.5 h-4.5 ${variant === "error" ? "text-red-400" : "text-[var(--color-text-muted)]"}`} />
        </div>
        <div>
          <p className="text-xs text-[var(--color-text-subtle)]">{label}</p>
          <p className={`text-xl font-semibold ${variant === "error" ? "text-red-400" : "text-[var(--color-text)]"}`}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          {subValue && <p className="text-xs text-[var(--color-text-subtle)]">{subValue}</p>}
        </div>
      </div>
    </Card>
  );
}

function ExportMenu({
  target,
  exporting,
  onExport,
}: {
  target: "requests" | "kv";
  exporting: string | null;
  onExport: (target: "requests" | "kv", format: "csv" | "json") => void;
}) {
  const [open, setOpen] = useState(false);
  const isExporting = exporting?.startsWith(target);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={!!isExporting}
        className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] bg-[var(--color-elevated)] rounded transition-colors disabled:opacity-50"
      >
        {isExporting ? (
          <LuLoader className="w-3 h-3 animate-spin" />
        ) : (
          <LuDownload className="w-3 h-3" />
        )}
        Export
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg py-1 min-w-[120px]">
            <button
              onClick={() => { setOpen(false); onExport(target, "csv"); }}
              className="w-full px-3 py-1.5 text-xs text-left text-[var(--color-text)] hover:bg-[var(--color-elevated)] transition-colors"
            >
              Download CSV
            </button>
            <button
              onClick={() => { setOpen(false); onExport(target, "json"); }}
              className="w-full px-3 py-1.5 text-xs text-left text-[var(--color-text)] hover:bg-[var(--color-elevated)] transition-colors"
            >
              Download JSON
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────

export function MonitoringPage() {
  const [range, setRange] = useState<TimeRange>("24h");
  const [source, setSource] = useState<SourceFilter>("all");
  const [stats, setStats] = useState<MonitoringStats | null>(null);
  const [timeline, setTimeline] = useState<MonitoringTimeline | null>(null);
  const [requests, setRequests] = useState<RequestLogResponse | null>(null);
  const [kvUsage, setKvUsage] = useState<KvUsageStats | null>(null);
  const [errorGroups, setErrorGroups] = useState<ErrorGroup[]>([]);
  const [endpointPerf, setEndpointPerf] = useState<EndpointPerformance[]>([]);
  const [emailStats, setEmailStats] = useState<EmailPipelineStats | null>(null);
  const [uptimeStats, setUptimeStats] = useState<UptimeStats | null>(null);
  const [doStats, setDoStats] = useState<DurableObjectStats | null>(null);
  const [dbSize, setDbSize] = useState<DbSizeStats | null>(null);
  const [alertsData, setAlertsData] = useState<AlertsData | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<RequestLogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(0);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Filters
  const [filterIp, setFilterIp] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [filterPath, setFilterPath] = useState("");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      const [statsData, timelineData, requestsData, kvData, errorsData, endpointsData, emailData, uptimeData, doData, dbData, alertData] = await Promise.all([
        api.adminMonitoring.stats(range, source),
        api.adminMonitoring.timeline(range, source),
        api.adminMonitoring.requests({
          page,
          limit: 25,
          status: filterStatus || undefined,
          method: filterMethod || undefined,
          path: filterPath || undefined,
          ip: filterIp || undefined,
          source: source !== "all" ? source : undefined,
        }),
        api.adminMonitoring.kv(range),
        api.adminMonitoring.errors(range, source),
        api.adminMonitoring.endpoints(range, source),
        api.adminMonitoring.email(range),
        api.adminMonitoring.uptime(range),
        api.adminMonitoring.durableObjects().catch(() => null),
        api.adminMonitoring.dbSize(range === "1h" || range === "6h" ? "24h" : range),
        api.adminMonitoring.alerts(),
      ]);
      setStats(statsData);
      setTimeline(timelineData);
      setRequests(requestsData);
      setKvUsage(kvData);
      setErrorGroups(errorsData.errors);
      setEndpointPerf(endpointsData.endpoints);
      setEmailStats(emailData);
      setUptimeStats(uptimeData);
      setDoStats(doData);
      setDbSize(dbData);
      setAlertsData(alertData);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load monitoring data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range, source, page, filterIp, filterStatus, filterMethod, filterPath]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current);
      autoRefreshRef.current = null;
    }
    if (autoRefresh > 0) {
      autoRefreshRef.current = setInterval(() => {
        fetchData(true);
      }, autoRefresh * 1000);
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [autoRefresh, fetchData]);

  const handleIpFilter = (ip: string) => {
    setFilterIp(ip);
    setPage(1);
    setShowFilters(true);
  };

  const clearFilters = () => {
    setFilterIp("");
    setFilterStatus("");
    setFilterMethod("");
    setFilterPath("");
    setPage(1);
  };

  const hasFilters = filterIp || filterStatus || filterMethod || filterPath;

  const [exporting, setExporting] = useState<string | null>(null);

  const handleExport = async (target: "requests" | "kv", format: "csv" | "json") => {
    const key = `${target}-${format}`;
    setExporting(key);
    try {
      const params = new URLSearchParams({ format, range });
      if (target === "requests") {
        if (filterStatus) params.set("status", filterStatus);
        if (filterMethod) params.set("method", filterMethod);
        if (filterPath) params.set("path", filterPath);
        if (filterIp) params.set("ip", filterIp);
        if (source !== "all") params.set("source", source);
      }
      const res = await fetch(`/console-api/admin/monitoring/export/${target}?${params}`);
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "csv" ? "csv" : "json";
      a.download = `${target}-${range}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silent fail
    } finally {
      setExporting(null);
    }
  };

  // Build chart data from timeline
  const chartData = useMemo(() => {
    if (!timeline || timeline.buckets.length === 0) return null;
    const buckets = timeline.buckets;
    return {
      labels: buckets.map((b) => b.time),
      requests: buckets.map((b) => b.requests),
      errors: buckets.map((b) => b.errors),
      avgMs: buckets.map((b) => b.avgResponseMs),
      p99Ms: buckets.map((b) => b.p99ResponseMs),
    };
  }, [timeline]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <LuLoader className="w-6 h-6 text-[var(--color-text-muted)] animate-spin" />
        <p className="text-sm text-[var(--color-text-muted)]">Loading monitoring data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Card padding="lg">
          <div className="text-center py-8">
            <p className="text-[var(--color-error)] mb-4">{error}</p>
            <Button variant="secondary" onClick={() => fetchData()}>Retry</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <header className="mb-6 space-y-4">
        {/* Title row */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-[var(--color-text)]">Monitoring</h1>
              {autoRefresh > 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-xs text-emerald-400 font-medium">
                  <LuRadio className="w-3 h-3 animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--color-text-muted)] mt-0.5">
              API request logs and KV usage tracking
              {lastUpdated && (
                <span className="text-[var(--color-text-subtle)] ml-2">
                  &middot; {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-text-subtle)] transition-colors disabled:opacity-50"
          >
            <LuRefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Controls row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Time range */}
          <div className="flex items-center bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
            {TIME_RANGES.map((tr) => (
              <button
                key={tr.value}
                onClick={() => { setRange(tr.value); setPage(1); }}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  range === tr.value
                    ? "bg-[var(--color-primary)] text-white"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                }`}
              >
                {tr.label}
              </button>
            ))}
          </div>

          <span className="hidden sm:block w-px h-5 bg-[var(--color-border)]" />

          {/* Source filter */}
          <div className="flex items-center bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
            {SOURCE_OPTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => { setSource(s.value); setPage(1); }}
                className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  source === s.value
                    ? "bg-[var(--color-primary)] text-white"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <span className="hidden sm:block w-px h-5 bg-[var(--color-border)]" />

          {/* Auto-refresh */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-[var(--color-text-subtle)] font-medium hidden sm:block">Refresh</span>
            <div className="flex items-center bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
              {AUTO_REFRESH_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setAutoRefresh(opt.value)}
                  className={`px-2 py-1.5 text-xs font-medium transition-colors ${
                    autoRefresh === opt.value
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={LuTrendingUp}
            label="Requests/min"
            value={stats.rpm.toFixed(1)}
          />
          <StatCard
            icon={LuServer}
            label="Total Requests"
            value={stats.totalRequests}
          />
          <StatCard
            icon={LuChartBar}
            label="Error Rate"
            value={`${stats.errorRate.toFixed(1)}%`}
            subValue={`${stats.errorCount} errors`}
            variant={stats.errorRate > 5 ? "error" : undefined}
          />
          <StatCard
            icon={LuGlobe}
            label="Avg Response"
            value={`${stats.avgResponseTime}ms`}
          />
        </div>
      )}

      {/* Charts Row */}
      {chartData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Request Volume Chart */}
          <Card padding="md">
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)] mb-3">Request Volume</h3>
            <LineChart
              labels={chartData.labels}
              series={[
                { data: chartData.requests, color: "#3b82f6", label: "Requests" },
                { data: chartData.errors, color: "#ef4444", label: "Errors" },
              ]}
            />
          </Card>

          {/* Response Time Chart */}
          <Card padding="md">
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)] mb-3">Response Time</h3>
            <LineChart
              labels={chartData.labels}
              series={[
                { data: chartData.avgMs, color: "#10b981", label: "Avg (ms)" },
                { data: chartData.p99Ms, color: "#f59e0b", label: "P99 (ms)" },
              ]}
              yFormat={(v) => `${Math.round(v)}ms`}
            />
          </Card>
        </div>
      )}

      {/* Status Breakdown + Top Endpoints */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Status Breakdown */}
          <Card padding="md">
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)] mb-3">Status Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(stats.statusBreakdown)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([status, count]) => {
                  const percent = stats.totalRequests > 0 ? (count / stats.totalRequests) * 100 : 0;
                  let barColor = "bg-[var(--color-text-subtle)]";
                  if (status.startsWith("2")) barColor = "bg-emerald-500";
                  else if (status.startsWith("3")) barColor = "bg-blue-500";
                  else if (status.startsWith("4")) barColor = "bg-amber-500";
                  else if (status.startsWith("5")) barColor = "bg-red-500";
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <span className="text-xs font-mono text-[var(--color-text-muted)] w-10">{status}</span>
                      <div className="flex-1 h-5 bg-[var(--color-elevated)] rounded overflow-hidden">
                        <div
                          className={`h-full ${barColor} rounded transition-all`}
                          style={{ width: `${Math.max(percent, 1)}%` }}
                        />
                      </div>
                      <span className="text-xs text-[var(--color-text-muted)] w-16 text-right">{count.toLocaleString()}</span>
                    </div>
                  );
                })}
              {Object.keys(stats.statusBreakdown).length === 0 && (
                <p className="text-sm text-[var(--color-text-muted)]">No data</p>
              )}
            </div>
          </Card>

          {/* Top Endpoints */}
          <Card padding="md">
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)] mb-3">Top Endpoints</h3>
            <div className="space-y-1.5">
              {stats.topEndpoints.slice(0, 10).map((ep, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded font-mono font-medium ${METHOD_COLORS[ep.method] || "bg-[var(--color-elevated)] text-[var(--color-text-muted)]"}`}>
                    {ep.method}
                  </span>
                  <span className="flex-1 text-[var(--color-text)] truncate font-mono">{ep.path}</span>
                  <span className="text-[var(--color-text-muted)]">{ep.count.toLocaleString()}</span>
                  <span className="text-[var(--color-text-subtle)] w-12 text-right">{ep.avgTime}ms</span>
                </div>
              ))}
              {stats.topEndpoints.length === 0 && (
                <p className="text-sm text-[var(--color-text-muted)]">No data</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Error Aggregation + Endpoint Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Error Aggregation */}
        <Card padding="md">
          <div className="flex items-center gap-2 mb-3">
            <LuTriangleAlert className="w-4 h-4 text-red-400" />
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">Top Errors</h3>
            {errorGroups.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">
                {errorGroups.reduce((s, e) => s + e.count, 0).toLocaleString()}
              </span>
            )}
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {errorGroups.slice(0, 10).map((err, i) => (
              <div key={i} className="p-2 rounded-lg bg-[var(--color-elevated)] border border-[var(--color-border)]">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium ${METHOD_COLORS[err.method] || ""}`}>
                      {err.method}
                    </span>
                    <span className="text-xs font-mono text-[var(--color-text)] truncate">{err.path}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={err.statusCode} />
                    <span className="text-xs font-medium text-red-400">&times;{err.count}</span>
                  </div>
                </div>
                <p className="text-[10px] text-red-400/80 font-mono truncate">{err.errorMessage}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-[var(--color-text-subtle)]">
                  <span>{err.affectedUsers} user{err.affectedUsers !== 1 ? "s" : ""}</span>
                  <span>Last: {new Date(err.lastSeen).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
            {errorGroups.length === 0 && (
              <p className="text-xs text-[var(--color-text-muted)] py-4 text-center">No errors in this period</p>
            )}
          </div>
        </Card>

        {/* Endpoint Performance */}
        <Card padding="md">
          <h3 className="text-sm font-medium text-[var(--color-text-subtle)] mb-3">Endpoint Performance</h3>
          <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--color-surface)]">
                <tr className="text-left text-[var(--color-text-subtle)]">
                  <th className="pb-2 font-medium">Endpoint</th>
                  <th className="pb-2 font-medium text-right">Reqs</th>
                  <th className="pb-2 font-medium text-right">Avg</th>
                  <th className="pb-2 font-medium text-right">P95</th>
                  <th className="pb-2 font-medium text-right">P99</th>
                  <th className="pb-2 font-medium text-right">Err%</th>
                  <th className="pb-2 font-medium text-right">Slow</th>
                </tr>
              </thead>
              <tbody>
                {endpointPerf.map((ep, i) => (
                  <tr key={i} className="border-t border-[var(--color-border)]">
                    <td className="py-1.5">
                      <div className="flex items-center gap-1">
                        <span className={`px-1 py-0.5 rounded text-[9px] font-mono font-medium ${METHOD_COLORS[ep.method] || ""}`}>
                          {ep.method}
                        </span>
                        <span className="font-mono text-[var(--color-text)] truncate max-w-[140px]">{ep.path}</span>
                      </div>
                    </td>
                    <td className="py-1.5 text-right text-[var(--color-text-muted)]">{ep.count.toLocaleString()}</td>
                    <td className="py-1.5 text-right text-[var(--color-text-muted)] font-mono">{ep.avgMs}</td>
                    <td className={`py-1.5 text-right font-mono ${ep.p95Ms >= 1000 ? "text-amber-400" : "text-[var(--color-text-muted)]"}`}>{ep.p95Ms}</td>
                    <td className={`py-1.5 text-right font-mono ${ep.p99Ms >= 1000 ? "text-red-400" : "text-[var(--color-text-muted)]"}`}>{ep.p99Ms}</td>
                    <td className={`py-1.5 text-right ${ep.errorRate > 5 ? "text-red-400" : "text-[var(--color-text-muted)]"}`}>{ep.errorRate}%</td>
                    <td className={`py-1.5 text-right ${ep.slowCount > 0 ? "text-amber-400" : "text-[var(--color-text-muted)]"}`}>{ep.slowCount}</td>
                  </tr>
                ))}
                {endpointPerf.length === 0 && (
                  <tr><td colSpan={7} className="py-4 text-center text-[var(--color-text-muted)]">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Email Pipeline + Uptime */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Email Pipeline */}
        {emailStats && (
          <Card padding="md">
            <div className="flex items-center gap-2 mb-3">
              <LuMail className="w-4 h-4 text-[var(--color-text-muted)]" />
              <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">Email Pipeline</h3>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {(["received", "stored", "forwarded", "rejected", "error"] as const).map((type) => {
                const count = emailStats.counts[type] || 0;
                let color = "text-[var(--color-text)]";
                if (type === "stored") color = "text-emerald-400";
                else if (type === "forwarded") color = "text-blue-400";
                else if (type === "rejected") color = "text-amber-400";
                else if (type === "error") color = "text-red-400";
                return (
                  <div key={type} className="p-2 rounded-lg bg-[var(--color-elevated)] text-center">
                    <p className={`text-lg font-semibold font-mono ${color}`}>{count}</p>
                    <p className="text-[10px] text-[var(--color-text-subtle)] capitalize">{type}</p>
                  </div>
                );
              })}
              <div className="p-2 rounded-lg bg-[var(--color-elevated)] text-center">
                <p className="text-lg font-semibold font-mono text-[var(--color-text)]">{emailStats.avgProcessingMs}ms</p>
                <p className="text-[10px] text-[var(--color-text-subtle)]">Avg Process</p>
              </div>
            </div>

            {/* Recent events */}
            <div className="space-y-1 max-h-[160px] overflow-y-auto">
              {emailStats.recentEvents.slice(0, 15).map((ev) => {
                let color = "text-[var(--color-text-muted)]";
                if (ev.eventType === "stored") color = "text-emerald-400";
                else if (ev.eventType === "forwarded") color = "text-blue-400";
                else if (ev.eventType === "rejected") color = "text-amber-400";
                else if (ev.eventType === "error") color = "text-red-400";
                return (
                  <div key={ev.id} className="flex items-center gap-2 text-[10px]">
                    <span className="text-[var(--color-text-subtle)] w-14 shrink-0">
                      {new Date(ev.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className={`w-16 shrink-0 font-medium ${color}`}>{ev.eventType}</span>
                    <span className="text-[var(--color-text-muted)] truncate">{ev.toAddress}</span>
                    {ev.durationMs != null && (
                      <span className="shrink-0 text-[var(--color-text-subtle)]">{ev.durationMs}ms</span>
                    )}
                  </div>
                );
              })}
              {emailStats.recentEvents.length === 0 && (
                <p className="text-xs text-[var(--color-text-muted)] py-4 text-center">No email events</p>
              )}
            </div>
          </Card>
        )}

        {/* Uptime */}
        {uptimeStats && (
          <Card padding="md">
            <div className="flex items-center gap-2 mb-3">
              <LuShield className="w-4 h-4 text-[var(--color-text-muted)]" />
              <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">Uptime</h3>
            </div>

            {/* Uptime percentage */}
            <div className="flex items-center gap-4 mb-4">
              <div className="text-center">
                <p className={`text-3xl font-bold font-mono ${
                  uptimeStats.uptimePercent >= 99.9 ? "text-emerald-400" :
                  uptimeStats.uptimePercent >= 99 ? "text-amber-400" : "text-red-400"
                }`}>
                  {uptimeStats.uptimePercent}%
                </p>
                <p className="text-[10px] text-[var(--color-text-subtle)]">Uptime</p>
              </div>
              <div className="flex-1 grid grid-cols-3 gap-2">
                {(["database", "kv", "r2"] as const).map((svc) => {
                  const pct = uptimeStats.serviceHealth[svc];
                  return (
                    <div key={svc} className="p-2 rounded-lg bg-[var(--color-elevated)] text-center">
                      <p className={`text-sm font-semibold font-mono ${pct >= 99.9 ? "text-emerald-400" : pct >= 99 ? "text-amber-400" : "text-red-400"}`}>
                        {pct}%
                      </p>
                      <p className="text-[10px] text-[var(--color-text-subtle)] uppercase">{svc === "database" ? "D1" : svc.toUpperCase()}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-4 mb-3 text-xs text-[var(--color-text-muted)]">
              <span>{uptimeStats.totalChecks} checks</span>
              <span className="text-emerald-400">{uptimeStats.okCount} ok</span>
              {uptimeStats.degradedCount > 0 && <span className="text-amber-400">{uptimeStats.degradedCount} degraded</span>}
              {uptimeStats.errorCount > 0 && <span className="text-red-400">{uptimeStats.errorCount} errors</span>}
              <span>avg {uptimeStats.avgResponseMs}ms</span>
            </div>

            {/* Recent checks timeline */}
            <div className="flex gap-px flex-wrap">
              {uptimeStats.recentChecks.slice(0, 60).reverse().map((check) => {
                let bg = "bg-emerald-500";
                if (check.status === "degraded") bg = "bg-amber-500";
                else if (check.status === "error") bg = "bg-red-500";
                return (
                  <div
                    key={check.id}
                    className={`w-2 h-5 rounded-sm ${bg} opacity-80 hover:opacity-100 transition-opacity`}
                    title={`${check.status} - ${new Date(check.createdAt).toLocaleString()} (${check.responseTimeMs}ms)`}
                  />
                );
              })}
              {uptimeStats.recentChecks.length === 0 && (
                <p className="text-xs text-[var(--color-text-muted)] py-4 text-center w-full">No uptime data yet - checks run every 5 minutes</p>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Durable Objects + Database Size + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Durable Objects */}
        <Card padding="md">
          <div className="flex items-center gap-2 mb-3">
            <LuWifi className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">Durable Objects</h3>
          </div>
          {doStats ? (
            <div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-lg font-bold text-[var(--color-text)]">{doStats.totalConnections}</p>
                  <p className="text-[10px] text-[var(--color-text-subtle)]">WebSocket Connections</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-[var(--color-text)]">{doStats.activeRooms}</p>
                  <p className="text-[10px] text-[var(--color-text-subtle)]">Active Rooms</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-[var(--color-text)]">{doStats.totalUniqueUsers}</p>
                  <p className="text-[10px] text-[var(--color-text-subtle)]">Unique Users</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-[var(--color-text)]">{doStats.totalTrackedMessages}</p>
                  <p className="text-[10px] text-[var(--color-text-subtle)]">Read Receipts</p>
                </div>
              </div>
              {doStats.rooms.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] text-[var(--color-text-subtle)] font-medium">Active Rooms</p>
                  {doStats.rooms.map((room) => (
                    <div key={room.inboxId} className="flex justify-between items-center text-xs py-1 border-t border-[var(--color-border)]">
                      <div>
                        <span className="text-[var(--color-text)] font-mono">{room.address}</span>
                        <span className="text-[var(--color-text-muted)] ml-1.5">({room.teamName})</span>
                      </div>
                      <span className="text-[var(--color-text-subtle)]">{room.connections} conn</span>
                    </div>
                  ))}
                </div>
              )}
              {doStats.totalConnections === 0 && (
                <p className="text-xs text-[var(--color-text-muted)] text-center py-2">No active WebSocket connections</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] text-center py-4">Loading...</p>
          )}
        </Card>

        {/* Database Size */}
        <Card padding="md">
          <div className="flex items-center gap-2 mb-3">
            <LuDatabase className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">Database Size</h3>
          </div>
          {dbSize ? (
            <div>
              <div className="mb-3">
                <p className="text-lg font-bold text-[var(--color-text)]">{dbSize.totalRows.toLocaleString()}</p>
                <p className="text-[10px] text-[var(--color-text-subtle)]">Total Rows</p>
              </div>
              <div className="space-y-1">
                {dbSize.tables
                  .sort((a, b) => b.rowCount - a.rowCount)
                  .slice(0, 8)
                  .map((t) => (
                    <div key={t.tableName} className="flex justify-between items-center text-xs">
                      <span className="text-[var(--color-text-muted)] font-mono truncate mr-2">{t.tableName}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-[var(--color-border)] rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              t.rowCount > 400000 ? "bg-red-400" : t.rowCount > 100000 ? "bg-amber-400" : "bg-emerald-400"
                            }`}
                            style={{ width: `${Math.min(100, (t.rowCount / Math.max(1, dbSize.tables[0]?.rowCount || 1)) * 100)}%` }}
                          />
                        </div>
                        <span className={`text-right w-16 tabular-nums ${
                          t.rowCount > 400000 ? "text-red-400" : "text-[var(--color-text)]"
                        }`}>
                          {t.rowCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] text-center py-4">Loading...</p>
          )}
        </Card>

        {/* Alerts */}
        <Card padding="md">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <LuBell className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">Alerts</h3>
            </div>
            <button
              onClick={async () => {
                try {
                  await api.adminMonitoring.testAlert();
                } catch { /* ignore */ }
              }}
              className="text-[10px] text-[var(--color-primary)] hover:underline"
            >
              Send Test
            </button>
          </div>
          {alertsData ? (
            <div>
              <div className="space-y-2 mb-3">
                {alertsData.configs.map((config) => {
                  const labels: Record<string, string> = {
                    error_rate: "5xx Error Rate",
                    uptime: "Uptime Drop",
                    d1_rows: "D1 Row Count",
                    email_errors: "Email Errors",
                  };
                  const thresholdLabels: Record<string, string> = {
                    error_rate: `>${config.thresholdValue}%`,
                    uptime: `<${config.thresholdValue}%`,
                    d1_rows: `>${config.thresholdValue.toLocaleString()} rows`,
                    email_errors: `>${config.thresholdValue}/15min`,
                  };
                  return (
                    <div key={config.alertType} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            await api.adminMonitoring.updateAlert(config.alertType, { enabled: !config.enabled });
                            const fresh = await api.adminMonitoring.alerts();
                            setAlertsData(fresh);
                          }}
                          className={`w-7 h-4 rounded-full transition-colors relative ${
                            config.enabled ? "bg-emerald-500" : "bg-[var(--color-border)]"
                          }`}
                        >
                          <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                            config.enabled ? "left-3.5" : "left-0.5"
                          }`} />
                        </button>
                        <span className="text-[var(--color-text)]">{labels[config.alertType] || config.alertType}</span>
                      </div>
                      <span className="text-[var(--color-text-muted)]">{thresholdLabels[config.alertType] || config.thresholdValue}</span>
                    </div>
                  );
                })}
              </div>
              {alertsData.history.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--color-text-subtle)] font-medium mb-1.5">Recent Alerts</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {alertsData.history.slice(0, 5).map((h) => (
                      <div key={h.id} className="text-[11px] py-1 border-t border-[var(--color-border)]">
                        <div className="flex justify-between">
                          <span className={`font-medium ${
                            h.alertType === "error_rate" || h.alertType === "email_errors" ? "text-red-400" : "text-amber-400"
                          }`}>
                            {h.alertType.replace("_", " ")}
                          </span>
                          <span className="text-[var(--color-text-muted)]">{new Date(h.createdAt).toLocaleDateString()}</span>
                        </div>
                        <p className="text-[var(--color-text-muted)] truncate">{h.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {alertsData.history.length === 0 && (
                <p className="text-xs text-[var(--color-text-muted)] text-center py-2">No alerts triggered yet</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] text-center py-4">Loading...</p>
          )}
        </Card>
      </div>

      {/* Top IPs */}
      {stats && stats.topIps.length > 0 && (
        <Card padding="md" className="mb-6">
          <h3 className="text-sm font-medium text-[var(--color-text-subtle)] mb-3">Top IPs by Request Count</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[var(--color-text-subtle)]">
                  <th className="pb-2 font-medium">IP Address</th>
                  <th className="pb-2 font-medium">Country</th>
                  <th className="pb-2 font-medium">City</th>
                  <th className="pb-2 font-medium text-right">Requests</th>
                  <th className="pb-2 font-medium text-right">Errors</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {stats.topIps.map((ip, i) => (
                  <tr key={i} className="border-t border-[var(--color-border)]">
                    <td className="py-2 font-mono text-[var(--color-text)]">{ip.ip}</td>
                    <td className="py-2 text-[var(--color-text-muted)]">{ip.country || "\u2014"}</td>
                    <td className="py-2 text-[var(--color-text-muted)]">{ip.city || "\u2014"}</td>
                    <td className="py-2 text-right text-[var(--color-text)]">{ip.count.toLocaleString()}</td>
                    <td className={`py-2 text-right ${ip.errorCount > 0 ? "text-red-400" : "text-[var(--color-text-muted)]"}`}>
                      {ip.errorCount}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => handleIpFilter(ip.ip)}
                        className="text-[var(--color-primary)] hover:underline"
                      >
                        Filter
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Request Log */}
      <Card padding="md" className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">Request Log</h3>
          <div className="flex items-center gap-2">
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] bg-[var(--color-elevated)] rounded"
              >
                <LuX className="w-3 h-3" />
                Clear filters
              </button>
            )}
            <ExportMenu
              target="requests"
              exporting={exporting}
              onExport={handleExport}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                showFilters ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              }`}
            >
              <LuFilter className="w-3 h-3" />
              Filters
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="flex flex-wrap gap-2 mb-3 p-3 bg-[var(--color-elevated)] rounded-lg">
            <div className="flex items-center gap-1">
              <LuSearch className="w-3.5 h-3.5 text-[var(--color-text-subtle)]" />
              <input
                type="text"
                placeholder="IP address"
                value={filterIp}
                onChange={(e) => { setFilterIp(e.target.value); setPage(1); }}
                className="w-32 px-2 py-1 text-xs bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-[var(--color-text)]"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              className="px-2 py-1 text-xs bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-[var(--color-text)]"
            >
              <option value="">All statuses</option>
              <option value="2xx">2xx Success</option>
              <option value="3xx">3xx Redirect</option>
              <option value="4xx">4xx Client Error</option>
              <option value="5xx">5xx Server Error</option>
            </select>
            <select
              value={filterMethod}
              onChange={(e) => { setFilterMethod(e.target.value); setPage(1); }}
              className="px-2 py-1 text-xs bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-[var(--color-text)]"
            >
              <option value="">All methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
            <input
              type="text"
              placeholder="Path contains..."
              value={filterPath}
              onChange={(e) => { setFilterPath(e.target.value); setPage(1); }}
              className="w-40 px-2 py-1 text-xs bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-[var(--color-text)]"
            />
          </div>
        )}

        {/* Table */}
        {requests && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[var(--color-text-subtle)]">
                    <th className="pb-2 font-medium">Time</th>
                    <th className="pb-2 font-medium">Method</th>
                    <th className="pb-2 font-medium">Path</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Source</th>
                    <th className="pb-2 font-medium text-right">Duration</th>
                    <th className="pb-2 font-medium">IP</th>
                    <th className="pb-2 font-medium">Country</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.requests.map((req) => (
                    <tr
                      key={req.id}
                      onClick={() => setSelectedRequest(selectedRequest?.id === req.id ? null : req)}
                      className="border-t border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-elevated)] transition-colors"
                    >
                      <td className="py-2 text-[var(--color-text-muted)] whitespace-nowrap">
                        {new Date(req.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="py-2">
                        <span className={`px-1.5 py-0.5 rounded font-mono font-medium ${METHOD_COLORS[req.method] || ""}`}>
                          {req.method}
                        </span>
                      </td>
                      <td className="py-2 font-mono text-[var(--color-text)] max-w-[200px] truncate">{req.path}</td>
                      <td className="py-2"><StatusBadge status={req.statusCode} /></td>
                      <td className="py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${SOURCE_COLORS[req.source] || "text-[var(--color-text-muted)]"}`}>
                          {req.source}
                        </span>
                      </td>
                      <td className="py-2 text-right text-[var(--color-text-muted)]">{req.responseTimeMs}ms</td>
                      <td className="py-2 font-mono text-[var(--color-text-muted)]">{req.ipAddress || "\u2014"}</td>
                      <td className="py-2 text-[var(--color-text-muted)]">{req.cfCountry || "\u2014"}</td>
                    </tr>
                  ))}
                  {requests.requests.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-[var(--color-text-muted)]">
                        No requests found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Selected Request Detail */}
            {selectedRequest && (
              <div className="mt-3 p-3 bg-[var(--color-elevated)] rounded-lg border border-[var(--color-border)]">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-[var(--color-text)]">Request Detail</h4>
                  <button onClick={() => setSelectedRequest(null)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                    <LuX className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-[var(--color-text-subtle)]">Request ID</span>
                    <p className="font-mono text-[var(--color-text)] break-all">{selectedRequest.requestId}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-subtle)]">Source</span>
                    <p className={`font-mono font-medium ${SOURCE_COLORS[selectedRequest.source] || "text-[var(--color-text)]"}`}>
                      {selectedRequest.source}
                    </p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-subtle)]">User ID</span>
                    <p className="font-mono text-[var(--color-text)]">{selectedRequest.userId || "Anonymous"}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-subtle)]">User Agent</span>
                    <p className="text-[var(--color-text)] truncate">{selectedRequest.userAgent || "\u2014"}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-subtle)]">Location</span>
                    <p className="text-[var(--color-text)]">
                      {[selectedRequest.cfCity, selectedRequest.cfCountry].filter(Boolean).join(", ") || "\u2014"}
                    </p>
                  </div>
                  {selectedRequest.errorMessage && (
                    <div className="col-span-full">
                      <span className="text-[var(--color-text-subtle)]">Error</span>
                      <p className="text-red-400 font-mono">{selectedRequest.errorMessage}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pagination */}
            {requests.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--color-border)]">
                <span className="text-xs text-[var(--color-text-muted)]">
                  Page {requests.pagination.page} of {requests.pagination.totalPages} ({requests.pagination.total.toLocaleString()} total)
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
                  >
                    <LuChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= requests.pagination.totalPages}
                    className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
                  >
                    <LuChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* KV Usage */}
      {kvUsage && (
        <Card padding="md">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-[var(--color-text-subtle)]">KV Usage</h3>
            <ExportMenu
              target="kv"
              exporting={exporting}
              onExport={handleExport}
            />
          </div>

          {/* KV Timeline Chart */}
          {kvUsage.timeline.length > 0 && (
            <div className="mb-4">
              <LineChart
                labels={kvUsage.timeline.map((t) => t.bucket)}
                series={[
                  { data: kvUsage.timeline.map((t) => t.count), color: "#8b5cf6", label: "Operations" },
                ]}
                height={140}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* By Operation */}
            <div>
              <h4 className="text-xs text-[var(--color-text-subtle)] mb-2">By Operation</h4>
              <div className="space-y-1.5">
                {kvUsage.byOperation.map((op, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-[var(--color-text)] font-mono">{op.operation}</span>
                    <span className="text-[var(--color-text-muted)]">{op.total.toLocaleString()}</span>
                  </div>
                ))}
                {kvUsage.byOperation.length === 0 && (
                  <p className="text-xs text-[var(--color-text-muted)]">No data</p>
                )}
              </div>
            </div>

            {/* By Key Pattern */}
            <div>
              <h4 className="text-xs text-[var(--color-text-subtle)] mb-2">Top Key Patterns</h4>
              <div className="space-y-1.5">
                {kvUsage.byKeyPattern.slice(0, 10).map((kp, i) => (
                  <div key={i} className="flex items-center justify-between text-xs gap-2">
                    <span className="text-[var(--color-text)] font-mono truncate">{kp.keyPattern}</span>
                    <span className="text-[var(--color-text-muted)] shrink-0">{kp.total.toLocaleString()}</span>
                  </div>
                ))}
                {kvUsage.byKeyPattern.length === 0 && (
                  <p className="text-xs text-[var(--color-text-muted)]">No data</p>
                )}
              </div>
            </div>

            {/* By Source */}
            <div>
              <h4 className="text-xs text-[var(--color-text-subtle)] mb-2">By Source</h4>
              <div className="space-y-1.5">
                {kvUsage.bySource.map((s, i) => {
                  let color = "text-[var(--color-text)]";
                  if (s.source === "l1_hit") color = "text-emerald-400";
                  else if (s.source === "kv") color = "text-blue-400";
                  else if (s.source === "d1_fallback") color = "text-amber-400";
                  return (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className={`font-mono ${color}`}>{s.source || "unknown"}</span>
                      <span className="text-[var(--color-text-muted)]">{s.total.toLocaleString()}</span>
                    </div>
                  );
                })}
                {kvUsage.bySource.length === 0 && (
                  <p className="text-xs text-[var(--color-text-muted)]">No data</p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
